import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Users, ArrowLeft, Copy, Check, Loader2, Sword } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Multiplayer Phase 2 — Party Co-op lobby.
 *
 * Two entry modes:
 *  - /party (no id)    → create-or-join hub
 *  - /party/:roomId    → lobby view (host or guest), auto-redirects to /dashboard once "playing"
 */
export default function PartyPage() {
  const { roomId } = useParams();
  return roomId ? <PartyLobby roomId={roomId} /> : <PartyHub />;
}

// ---------- HUB ----------
function PartyHub() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const [authing, setAuthing] = useState(true);

  // Ensure a session exists — auto-create a guest session if the visitor has none.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await api.get("/auth/me");
      } catch (e) {
        if (e?.response?.status === 401) {
          try { await api.post("/auth/guest"); } catch (_e) { /* silent */ }
        }
      }
      if (!cancelled) setAuthing(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const create = async () => {
    setCreating(true);
    try {
      const room = await api.post("/party/create");
      navigate(`/party/${room.room_id}`);
    } catch (e) {
      if (e?.response?.status === 401) {
        // Auth expired between mount and click — try one auto-guest and retry
        try {
          await api.post("/auth/guest");
          const room = await api.post("/party/create");
          navigate(`/party/${room.room_id}`);
          return;
        } catch (_e) { /* fall through */ }
      }
      toast.error(e?.response?.data?.detail || "Could not open a party");
    } finally {
      setCreating(false);
    }
  };

  const join = async () => {
    const c = code.trim().toUpperCase();
    if (!c) return;
    setJoining(true);
    try {
      const room = await api.post("/party/join", { code: c });
      navigate(`/party/${room.room_id}`);
    } catch (e) {
      if (e?.response?.status === 401) {
        try {
          await api.post("/auth/guest");
          const room = await api.post("/party/join", { code: c });
          navigate(`/party/${room.room_id}`);
          return;
        } catch (_e) { /* fall through */ }
      }
      toast.error(e?.response?.data?.detail || "No such party — check the code");
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="party-hub">
      <div className="max-w-2xl w-full">
        <button
          onClick={() => navigate("/")}
          className="btn-glow text-xs tracking-[0.3em] flex items-center gap-2 mb-8"
          data-testid="party-back-btn"
        >
          <ArrowLeft size={14} /> BACK
        </button>

        <div className="text-center mb-8">
          <div className="text-xs tracking-[0.5em] blood-text flex items-center justify-center gap-2 mb-3">
            <Users size={14} /> PARTY CO-OP <Users size={14} />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl gold-text">Descend Together</h1>
          <p className="ash-text mt-3 text-sm italic max-w-md mx-auto">
            Two heroes. Alternating turns. One shared fate. When either falls, both perish.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {/* CREATE */}
          <button
            onClick={create}
            disabled={creating}
            className="panel p-6 text-left hover:scale-[1.02] transition-transform border-2"
            style={{ borderColor: "rgba(214,158,71,0.5)" }}
            data-testid="party-create-btn"
          >
            <div className="text-xs tracking-[0.3em] gold-text mb-2">HOST A PARTY</div>
            <div className="font-heading text-2xl">Open the Gate</div>
            <div className="text-xs ash-text mt-2">
              Create a room, get a code. Share it with your ally.
              You&apos;ll pick the class and go first.
            </div>
            {creating && <Loader2 className="animate-spin mt-3 ash-text" size={16} />}
          </button>

          {/* JOIN */}
          <div className="panel p-6 border-2" style={{ borderColor: "rgba(217,101,52,0.5)" }} data-testid="party-join-panel">
            <div className="text-xs tracking-[0.3em] blood-text mb-2">JOIN AN ALLY</div>
            <div className="font-heading text-2xl mb-3">Take Their Hand</div>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="EMB-XXXX"
              className="w-full bg-black/50 border border-[#d4af37]/40 rounded px-3 py-2 tracking-[0.3em] text-center font-heading text-lg focus:border-[#d69e47] outline-none"
              maxLength={9}
              data-testid="party-code-input"
              onKeyDown={(e) => { if (e.key === "Enter") join(); }}
            />
            <button
              onClick={join}
              disabled={joining || !code.trim()}
              className="btn-primary w-full mt-3 text-xs tracking-[0.3em]"
              data-testid="party-join-btn"
            >
              {joining ? <Loader2 className="animate-spin inline mr-2" size={12} /> : null}
              JOIN PARTY
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- LOBBY ----------
function PartyLobby({ roomId }) {
  const navigate = useNavigate();
  const [room, setRoom] = useState(null);
  const [copied, setCopied] = useState(false);
  const [starting, setStarting] = useState(false);
  const [me, setMe] = useState(null);

  // Load current user once
  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r?.user || r)).catch(() => {});
  }, []);

  // Poll room state every 2s while in lobby
  const load = useCallback(async () => {
    try {
      const r = await api.get(`/party/status/${roomId}`);
      setRoom(r);
      // Auto-redirect once the host starts
      if (r.status === "playing") {
        navigate("/dashboard");
      } else if (r.status === "ended") {
        toast("The party has disbanded.");
        navigate("/party");
      }
    } catch (e) {
      if (e?.response?.status === 404) {
        toast.error("Party no longer exists");
        navigate("/party");
      }
    }
  }, [roomId, navigate]);

  useEffect(() => {
    load();
    const t = setInterval(load, 2500);
    return () => clearInterval(t);
  }, [load]);

  const copyCode = async () => {
    if (!room?.code) return;
    try {
      await navigator.clipboard.writeText(room.code);
      setCopied(true);
      toast.success("Code copied");
      setTimeout(() => setCopied(false), 1400);
    } catch {
      toast.error("Copy failed — long-press the code to copy manually");
    }
  };

  const startParty = async () => {
    setStarting(true);
    try {
      await api.post(`/party/${roomId}/start`);
      navigate("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the party");
      setStarting(false);
    }
  };

  const leaveParty = async () => {
    try { await api.post(`/party/${roomId}/leave`); } catch (_e) { /* ignore */ }
    navigate("/party");
  };

  if (!room || !me) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin ash-text" size={32} />
      </div>
    );
  }

  const isHost = me.user_id === room.host_user_id;
  const bothPresent = Boolean(room.host_user_id && room.guest_user_id);

  return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="party-lobby">
      <div className="max-w-3xl w-full">
        <div className="text-center mb-6">
          <div className="text-xs tracking-[0.4em] blood-text flex items-center justify-center gap-2 mb-2">
            <Users size={14} /> PARTY LOBBY
          </div>
          <h1 className="font-heading text-3xl md:text-4xl gold-text">
            {isHost ? "Awaiting Your Ally" : "You Have Answered the Call"}
          </h1>
        </div>

        {/* CODE */}
        <div className="panel p-6 mb-4 text-center" data-testid="party-code-display">
          <div className="text-[10px] tracking-[0.4em] ash-text mb-2">SHARE THIS CODE</div>
          <div className="flex items-center justify-center gap-3">
            <div className="font-heading text-5xl gold-text tracking-[0.15em]" data-testid="party-code-value">
              {room.code}
            </div>
            <button
              onClick={copyCode}
              className="btn-glow p-2"
              title="Copy code"
              data-testid="party-copy-btn"
            >
              {copied ? <Check size={18} className="text-green-400" /> : <Copy size={18} />}
            </button>
          </div>
        </div>

        {/* HERO SLOTS */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <HeroSlot
            label="HOST"
            filled={!!room.host_user_id}
            name={room.host_display}
            isMe={isHost}
          />
          <HeroSlot
            label="ALLY"
            filled={!!room.guest_user_id}
            name={room.guest_display}
            isMe={!isHost && !!room.guest_user_id}
          />
        </div>

        {/* ACTIONS */}
        <div className="flex justify-between items-center gap-4">
          <button
            onClick={leaveParty}
            className="btn-glow text-xs tracking-[0.3em]"
            data-testid="party-leave-btn"
          >
            LEAVE PARTY
          </button>

          {isHost ? (
            <button
              onClick={startParty}
              disabled={!bothPresent || starting}
              className="btn-primary text-xs tracking-[0.3em] flex items-center gap-2 disabled:opacity-40"
              data-testid="party-start-btn"
            >
              {starting ? <Loader2 className="animate-spin" size={12} /> : <Sword size={14} />}
              {bothPresent ? "BEGIN THE DESCENT" : "WAITING FOR ALLY..."}
            </button>
          ) : (
            <span className="text-xs tracking-[0.3em] ash-text italic" data-testid="party-waiting-host">
              WAITING FOR HOST TO BEGIN...
            </span>
          )}
        </div>

        {isHost && (
          <p className="text-center text-[10px] tracking-[0.3em] ash-text mt-6">
            YOU MUST START A RUN FIRST · THEN INVITE YOUR ALLY
          </p>
        )}
      </div>
    </div>
  );
}

function HeroSlot({ label, filled, name, isMe }) {
  return (
    <div
      className={`panel p-4 text-center border-2 transition-all ${filled ? "opacity-100" : "opacity-50"}`}
      style={{ borderColor: filled ? "rgba(214,158,71,0.5)" : "rgba(120,120,120,0.2)" }}
      data-testid={`party-slot-${label.toLowerCase()}`}
    >
      <div className="text-[10px] tracking-[0.4em] blood-text mb-2">{label}</div>
      {filled ? (
        <>
          <div className="font-heading text-xl gold-text truncate">{name || "Wanderer"}</div>
          {isMe && <div className="text-[10px] tracking-[0.3em] ash-text mt-1">YOU</div>}
        </>
      ) : (
        <div className="font-heading text-xl ash-text italic">— empty —</div>
      )}
    </div>
  );
}
